import { z } from 'zod'

import { http } from '@/shared/api'

import type { GlobalConfig } from '../model/types'

/*
 * `.nullable()` на каждом списке: колонки объявлены `NOT NULL DEFAULT '{}'`, но
 * пустой массив приезжает из pgx как nil-слайс, а Go пишет nil как `null`.
 * Схема, требующая массив, падала бы ровно на очищенном списке.
 */
const configSchema = z.object({
  blacklist_ua: z.array(z.string()).nullable(),
  blacklist_isp: z.array(z.string()).nullable(),
  blacklist_dns: z.array(z.string()).nullable(),
  vpn_isp: z.array(z.string()).nullable(),
  vpn_dns: z.array(z.string()).nullable(),
  blacklist_ip: z.array(z.string()).nullable(),
  updated_at: z.string(),
})

const envelopeSchema = z.object({ global_config: configSchema })

const toDomain = (raw: z.infer<typeof configSchema>): GlobalConfig => ({
  blacklistUa: raw.blacklist_ua ?? [],
  blacklistIsp: raw.blacklist_isp ?? [],
  blacklistDns: raw.blacklist_dns ?? [],
  vpnIsp: raw.vpn_isp ?? [],
  vpnDns: raw.vpn_dns ?? [],
  blacklistIp: raw.blacklist_ip ?? [],
  updatedAt: raw.updated_at,
})

export async function fetchGlobalConfig(signal?: AbortSignal): Promise<GlobalConfig> {
  const { global_config: raw } = await http.get('admin/global-config', {
    schema: envelopeSchema,
    signal,
  })
  return toDomain(raw)
}

/**
 * PATCH по имени, замена по существу: сервер пишет все шесть колонок одним
 * upsert-ом, и поле, которого нет в теле, становится пустым списком. Поэтому
 * уходят все шесть, всегда.
 *
 * Ответ разбирается и возвращается: `blacklist_ip` хранится в колонке `CIDR[]`,
 * так что набранное `1.2.3.4` вернётся как `1.2.3.4/32` — показать надо то, что
 * записалось, а не то, что набрали.
 */
export async function updateGlobalConfig(config: GlobalConfig): Promise<GlobalConfig> {
  const { global_config: raw } = await http.patch('admin/global-config', {
    schema: envelopeSchema,
    json: {
      blacklist_ua: config.blacklistUa,
      blacklist_isp: config.blacklistIsp,
      blacklist_dns: config.blacklistDns,
      vpn_isp: config.vpnIsp,
      vpn_dns: config.vpnDns,
      blacklist_ip: config.blacklistIp,
    },
  })
  return toDomain(raw)
}
